
import { useState } from "react";
import { validateForm, validateField } from "../../utils/formValidator";
import apiService from "../../services/apiService";

/**
 * Custom hook to handle CRUD operations for any module.
 */
export function useCRUDOperations(
  endpoint,
  initialData = [],
  formFields = [],
  customHandlers = {},
  onDataChange,
  deleteItemFn,
  createItemFn,
  updateItemFn,
  refreshDataFn,
  successMessages = {},
  title = "Item"
) {
  const [viewState, setViewState] = useState({ mode: "list", item: null });
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    item: null,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  // ✅ FIX 1: Correctly initialize form data (supports nested objects → IDs)
  const initializeFormData = (item = null) => {
    const defaults = {};
    formFields.forEach((field) => {
    defaults[field.name] =
  item?.[field.name]?.value ??
  item?.[field.name]?.id ??
  item?.[field.name] ??
  field.default ??
  "";

    });
    return defaults;
  };

  // --------------------
  // View Handlers
  // --------------------
  const handleAdd = () => {
    setFormData(initializeFormData());
    setErrors({});
    setViewState({ mode: "add", item: null });
  };

  const handleView = async (item) => {
    if (customHandlers?.onView) {
      try {
        const detailedItem = await customHandlers.onView(item);
        setFormData(initializeFormData(detailedItem));
        setViewState({ mode: "view", item: detailedItem });
        return;
      } catch (error) {
        console.error("Error fetching item details:", error);
      }
    }
    setFormData(initializeFormData(item));
    setViewState({ mode: "view", item });
  };

  const handleEdit = (item) => {
    setFormData(initializeFormData(item));
    setErrors({});
    setViewState({ mode: "edit", item });
  };

  const handleEditFromView = () => {
    setViewState({ mode: "edit", item: viewState.item });
  };

  const handleBackToList = () => {
    setViewState({ mode: "list", item: null });
    setFormData(initializeFormData());
    setErrors({});
  };

  const handleCancelEdit = () => {
    if (viewState.mode === "edit" && viewState.item) {
      setFormData(initializeFormData(viewState.item));
      setViewState({ mode: "view", item: viewState.item });
    } else {
      handleBackToList();
    }
    setErrors({});
  };

  // --------------------
  // Delete
  // --------------------
  const handleDelete = (item) => {
    setConfirmationModal({ isOpen: true, item });
  };

  const confirmDelete = async () => {
    if (!confirmationModal.item) return;

    setIsDeleting(true);
    try {
      console.log('🗑️ Starting delete for item:', confirmationModal.item);
      
      if (deleteItemFn) {
        console.log('✅ Using deleteItemFn');
        await deleteItemFn(confirmationModal.item.id);
      } else {
        console.log('✅ Using apiService.delete');
        await apiService.delete(endpoint, confirmationModal.item.id);
      }

      console.log('✅ Delete successful, refreshing data...');
      
      // Force refresh data after delete
      if (refreshDataFn) {
        await refreshDataFn();
        console.log('✅ Data refreshed');
      }

      if (viewState.item?.id === confirmationModal.item.id) {
        setViewState({ mode: "list", item: null });
      }

      setConfirmationModal({ isOpen: false, item: null });
      setToast({ show: true, message: `${title} deleted successfully!`, type: "success" });
      setTimeout(() => {
        setToast({ show: false, message: "", type: "success" });
      }, 2000);
    } catch (error) {
      console.error('❌ Delete failed:', error);
      setConfirmationModal({ isOpen: false, item: null });
      setToast({ show: true, message: error.message || "Failed to delete", type: "error" });
      setTimeout(() => {
        setToast({ show: false, message: "", type: "error" });
      }, 3000);
    } finally {
      setIsDeleting(false);
    }
  };

  // --------------------
  // Form change
  // --------------------
  const handleFormChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => {
      const updated = { ...prev, [name]: value };

      // ✅ FIX 2: Proper dependent reset (Center use-case)
      if (name === "districtId") {
        updated.talukaId = "";
        updated.villageId = "";
      }
      if (name === "talukaId") {
        updated.villageId = "";
      }

      return updated;
    });

    const fieldError = validateField(name, value, formFields);
    setErrors((prev) => ({ ...prev, [name]: fieldError ?? undefined }));
  };

  // --------------------
  // Submit
  // --------------------
  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    const { preventDefault: _, ...passedValues } = e || {};
    const dataToSubmit = Object.keys(passedValues).length > 0 ? passedValues : formData;
    const validationErrors = validateForm(dataToSubmit, formFields);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    setIsSubmitting(true);

    try {
      const processedData = customHandlers?.processFormData
        ? await customHandlers.processFormData(dataToSubmit, viewState.mode)
        : dataToSubmit;

      if (viewState.mode === "add") {
        if (createItemFn) {
          await createItemFn(processedData);
        } else {
          await apiService.create(endpoint, processedData);
        }
      }

      if (viewState.mode === "edit") {
        if (updateItemFn) {
          await updateItemFn(viewState.item.id, processedData);
        } else {
          await apiService.update(endpoint, viewState.item.id, processedData);
        }
        if (refreshDataFn) await refreshDataFn();
        setViewState({ mode: "list", item: null });
        setToast({ show: true, message: "Updated successfully!", type: "success" });
      }

      setTimeout(() => setToast({ show: false, message: "", type: "success" }), 2000);
    } catch (error) {
      setErrors({ submit: error.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    viewState,
    formData,
    errors,
    confirmationModal,
    isSubmitting,
    isDeleting,
    toast,
    setFormData,
    setErrors,
    setConfirmationModal,
    handleAdd,
    handleView,
    handleEdit,
    handleEditFromView,
    handleDelete,
    confirmDelete,
    handleBackToList,
    handleFormChange,
    handleSubmit,
    handleCancelEdit,
  };
}
